import React, { useContext, useEffect, useMemo, useState } from "react"
import moment from "moment"
import "./statistic-highlights.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Button from "../../components/ui/button/button"
import Segmented from "../../components/ui/segmented/segmented"
import Datepicker from "../../components/ui/datepicker/datepicker"
import { LoadingIcon, InformationIcon } from "../../utils/svgs"

// Context + access control
import { AuthContext } from "../../utils/providers/auth"
import { ModalContext } from "../../utils/providers/modal"
import { auth } from "../../utils/firebase"
import { isSuperAdminAllowlisted } from "../../utils/exports/super-admin-allowlist"

// Tabs
import AgentLeaderboard from "./_components/agent-leaderboard"
import GroupSizeConversion from "./_components/group-size-conversion"
import MissedBusiness from "./_components/missed-business"

// Data layer
import { fetchActiveAgents, fetchPeriodDataset, buildHistoryMapForClients, fetchMarginBaseline, rate, formatRate, splitNewRepeat, gbp } from "./_utils/data"

const TABS = {
	leaderboard: "Agent leaderboard",
	groupSize: "By group size",
	missed: "Missed business",
}

const PRESETS = [
	{ key: "30d", label: "Last 30 days", build: () => ({ start: moment().subtract(30, "days").startOf("day"), end: moment().endOf("day") }) },
	{ key: "90d", label: "Last 90 days", build: () => ({ start: moment().subtract(90, "days").startOf("day"), end: moment().endOf("day") }) },
	{ key: "180d", label: "Last 6 months", build: () => ({ start: moment().subtract(6, "months").startOf("day"), end: moment().endOf("day") }) },
	{ key: "365d", label: "Last 12 months", build: () => ({ start: moment().subtract(12, "months").startOf("day"), end: moment().endOf("day") }) },
	{ key: "ytd", label: "This year", build: () => ({ start: moment().startOf("year"), end: moment().endOf("day") }) },
	{ key: "last-year", label: "Last year", build: () => ({ start: moment().subtract(1, "year").startOf("year"), end: moment().subtract(1, "year").endOf("year") }) },
]

export default function StatisticHighlights() {
	const { user } = useContext(AuthContext)
	const { showModal } = useContext(ModalContext)
	const canAccess = Boolean(user?.is_super_admin) && isSuperAdminAllowlisted(auth.currentUser?.uid)

	const [agents, setAgents] = useState([])
	const [startDate, setStartDate] = useState(moment().subtract(90, "days").startOf("day").toDate())
	const [endDate, setEndDate] = useState(moment().endOf("day").toDate())

	const [loading, setLoading] = useState(false)
	const [hasFetched, setHasFetched] = useState(false)
	const [dataset, setDataset] = useState(null)
	const [historyMap, setHistoryMap] = useState(new Map())
	const [marginBaseline, setMarginBaseline] = useState(null)
	const [activeTab, setActiveTab] = useState("leaderboard")
	const [fetchedRange, setFetchedRange] = useState(null)

	useEffect(() => {
		if (!canAccess) return
		fetchActiveAgents()
			.then(setAgents)
			.catch((error) => console.error("Error loading agents:", error))
	}, [canAccess])

	const fetchData = async () => {
		setLoading(true)
		try {
			const startMs = moment(startDate).startOf("day").valueOf()
			const endMs = moment(endDate).endOf("day").valueOf()

			const periodDataset = await fetchPeriodDataset(startMs, endMs)

			const relevantClients = [...periodDataset.finalEnquiries.map((enquiry) => enquiry.client), ...periodDataset.allBookings.map((booking) => booking.client)]

			const [history, baseline] = await Promise.all([buildHistoryMapForClients(relevantClients), fetchMarginBaseline(moment().valueOf())])

			setDataset(periodDataset)
			setHistoryMap(history)
			setMarginBaseline(baseline)
			setFetchedRange({ startMs, endMs })
			setHasFetched(true)
		} catch (error) {
			console.error("Error loading statistic highlights:", error)
		} finally {
			setLoading(false)
		}
	}

	const applyPreset = (preset) => {
		const { start, end } = preset.build()
		setStartDate(start.toDate())
		setEndDate(end.toDate())
	}

	const summary = useMemo(() => {
		if (!dataset) return null

		const enquiries = dataset.finalEnquiries.length
		const bookings = dataset.allBookings.length
		const enquirySplit = splitNewRepeat(dataset.finalEnquiries, historyMap)
		const bookingSplit = splitNewRepeat(dataset.allBookings, historyMap)
		const totalMargin = dataset.allBookings.reduce((acc, booking) => acc + (Number(booking.margins?.profit) || 0), 0)

		return {
			enquiries,
			bookings,
			conversion: rate(bookings, enquiries),
			newConversion: rate(bookingSplit.newCount, enquirySplit.newCount),
			repeatConversion: rate(bookingSplit.repeatCount, enquirySplit.repeatCount),
			totalMargin,
		}
	}, [dataset, historyMap])

	const showInfoModal = () => {
		showModal({
			title: "How these numbers are worked out",
			body: (
				<div className="info-modal-content">
					<h4>Enquiries (de-duped)</h4>
					<ul>
						<li>Open enquiries and removed/closed enquiries within the selected range are combined, excluding test accounts and anything archived from stats.</li>
						<li>An enquiry counts as booked when a booking matches the same client, location and enquiry-created time.</li>
						<li>Non-booked enquiries are de-duplicated to one per client, and dropped entirely if that client booked anything - identical to the Conversions page.</li>
					</ul>

					<h4>Conversion rate</h4>
					<ul>
						<li>
							<strong>Bookings ÷ de-duped enquiries × 100.</strong> New vs repeat is decided by whether the client had an earlier booking before they enquired.
						</li>
					</ul>

					<h4>By group size</h4>
					<ul>
						<li>Both enquiries and bookings are bucketed on the same group_size figure (golfers + non-golfers), so each band's conversion is like-for-like.</li>
						<li>Buckets: 1-2, 3-4, 5-8, 9-12, 13-15, 16+.</li>
					</ul>

					<h4>Missed business</h4>
					<ul>
						<li>Average margin per booking comes from the last 6 months of bookings, grouped by the same group sizes (the same per-booking margin model the pipeline forecast uses).</li>
						<li>
							For each band: <strong>extra bookings = (target conversion × enquiries) − actual bookings</strong>, and <strong>missed business = extra bookings × average margin per booking</strong>.
						</li>
						<li>Larger groups make more per booking, so their average margin is naturally higher - the projection accounts for this automatically.</li>
					</ul>
				</div>
			),
			cancel: { label: "Close", action: () => {} },
		})
	}

	if (!canAccess) {
		return (
			<Tile fullPage={true}>
				<Title className="flex">
					<h1>Statistic Highlights</h1>
				</Title>
				<p className="stat-highlights-denied">You do not have access to this page.</p>
			</Tile>
		)
	}

	const kpiCards = summary
		? [
				{ label: "Enquiries (de-duped)", value: summary.enquiries.toLocaleString() },
				{ label: "Bookings", value: summary.bookings.toLocaleString() },
				{ label: "Overall conversion", value: formatRate(summary.conversion), emphasis: true },
				{ label: "New-client conversion", value: formatRate(summary.newConversion) },
				{ label: "Repeat-client conversion", value: formatRate(summary.repeatConversion) },
				{ label: "Margin booked", value: gbp.format(summary.totalMargin) },
		  ]
		: []

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Statistic Highlights</h1>
				<div
					className="info-icon-wrapper"
					onClick={showInfoModal}
					title="How are these calculations made?">
					<InformationIcon />
				</div>
			</Title>

			<div className="stat-highlights-controls">
				<div className="date-picker-group">
					<div className="date-picker-wrapper">
						<label>From:</label>
						<Datepicker
							placeholder="Start date"
							value={moment(startDate).valueOf()}
							onSelect={(ms) => setStartDate(moment(ms).startOf("day").toDate())}
						/>
					</div>
					<div className="date-picker-wrapper">
						<label>To:</label>
						<Datepicker
							placeholder="End date"
							value={moment(endDate).valueOf()}
							onSelect={(ms) => setEndDate(moment(ms).endOf("day").toDate())}
						/>
					</div>
					<Button
						label="Fetch"
						small={true}
						loading={loading}
						loadingText="Fetching..."
						onClick={fetchData}
					/>
				</div>

				<div className="preset-group">
					{PRESETS.map((preset) => (
						<button
							key={preset.key}
							type="button"
							className="preset-chip"
							onClick={() => applyPreset(preset)}>
							{preset.label}
						</button>
					))}
				</div>
			</div>

			{fetchedRange && (
				<div className="fetched-range-info">
					Showing {moment(fetchedRange.startMs).format("D MMM YYYY")} - {moment(fetchedRange.endMs).format("D MMM YYYY")}
				</div>
			)}

			{loading && (
				<div className="table-loading-splash">
					<div className="loading-wrapper">
						<p>Crunching the numbers...</p>
						<div className="svg-loading-wrap">
							<LoadingIcon />
						</div>
					</div>
				</div>
			)}

			{!loading && !hasFetched && <p className="stat-highlights-empty">Choose a date range (or a preset) and hit Fetch to build the report.</p>}

			{!loading && hasFetched && summary && (
				<>
					<div className="kpi-grid">
						{kpiCards.map((card) => (
							<div
								key={card.label}
								className={["kpi-card", card.emphasis ? "is-emphasis" : ""].join(" ").trim()}>
								<span className="kpi-value">{card.value}</span>
								<span className="kpi-label">{card.label}</span>
							</div>
						))}
					</div>

					<Segmented
						className="stat-highlights-tabs"
						options={TABS}
						selected={activeTab}
						onSelect={({ option }) => setActiveTab(option)}
					/>

					<div className="stat-highlights-tab-body">
						{activeTab === "leaderboard" && (
							<AgentLeaderboard
								agents={agents}
								dataset={dataset}
								historyMap={historyMap}
							/>
						)}
						{activeTab === "groupSize" && (
							<GroupSizeConversion
								agents={agents}
								dataset={dataset}
							/>
						)}
						{activeTab === "missed" && (
							<MissedBusiness
								dataset={dataset}
								marginBaseline={marginBaseline}
							/>
						)}
					</div>
				</>
			)}
		</Tile>
	)
}
